import { camelCase } from 'change-case';

import {
    buildAdvancedFiltersJsonLogic,
    parseAdvancedFilterDefinition,
    parseAdvancedFilterDefinitions,
    type AdvancedFilterDefinition,
} from './advancedFilters';
import {
    compactJsonLogicConditions,
    createFilterEnvelope,
    isFilterEnvelope,
    isJsonLogicRuleEmpty,
    isRecord,
    jsonLogicField,
    unwrapFilterEnvelope,
    type JsonLogicRule,
} from './jsonLogic';

export type SearchFiltersValue = Record<string, unknown>;
export type SearchFilterGroup = 'eventFilters' | 'premiumFilters';
export type SearchFilterJoin = 'all' | 'any';
export type SearchFilterOperator = 'contains' | 'equals' | 'greaterThan' | 'lessThan' | 'notEquals';

export type SearchFilterOperatorOption = {
    label: string;
    value: SearchFilterOperator;
};

export type SearchFilterEntry = {
    group?: SearchFilterGroup;
    key: string;
    path: string;
    value: unknown;
};
export type SearchAdvancedFilterRule = SearchFilterEntry & {
    operator: SearchFilterOperator;
    /** Exact UI field path used to create the rule. The compiled rule remains backend-portable. */
    sourcePath?: string[];
};

const FILTER_GROUP_KEYS = new Set<SearchFilterGroup>(['eventFilters', 'premiumFilters']);
const ENVELOPE_KEYS = new Set([
    'advancedFilter',
    'advancedFilters',
    'domain',
    'jsonLogic',
    'legacy',
    'metadata',
    'rules',
    'schemaVersion',
]);
const SEARCH_FILTER_OPERATORS = new Set<SearchFilterOperator>([
    'contains',
    'equals',
    'greaterThan',
    'lessThan',
    'notEquals',
]);

const TEXT_OPERATOR_OPTIONS: SearchFilterOperatorOption[] = [
    { label: 'Contains', value: 'contains' },
    { label: 'Equals', value: 'equals' },
    { label: 'Does Not Equal', value: 'notEquals' },
];

const NUMBER_OPERATOR_OPTIONS: SearchFilterOperatorOption[] = [
    { label: 'Equals', value: 'equals' },
    { label: 'Greater Than', value: 'greaterThan' },
    { label: 'Less Than', value: 'lessThan' },
    { label: 'Does Not Equal', value: 'notEquals' },
];

const hasFilterValue = (value: unknown): boolean => {
    if (Array.isArray(value)) {
        return value.length > 0;
    }

    if (isRecord(value)) {
        return Object.values(value).some(hasFilterValue);
    }

    return value !== '' && value !== null && value !== undefined && value !== false;
};

export const unwrapSearchFilters = (filters: unknown): SearchFiltersValue => {
    const value = unwrapFilterEnvelope<SearchFiltersValue>(filters);

    return isRecord(value) ? (value as SearchFiltersValue) : {};
};

export const getSearchFilterEntries = (filters: unknown): SearchFilterEntry[] => {
    const legacyFilters = unwrapSearchFilters(filters);
    const getGroupValue = (group: SearchFilterGroup) =>
        isRecord(legacyFilters[group]) ? (legacyFilters[group] as Record<string, unknown>) : {};
    const attributeEntries = Object.entries(legacyFilters)
        .filter(([key]) => !FILTER_GROUP_KEYS.has(key as SearchFilterGroup) && !ENVELOPE_KEYS.has(key))
        .filter(([, value]) => hasFilterValue(value))
        .map(([key, value]) => ({
            key,
            path: camelCase(key),
            value,
        }));
    const groupedEntries = Array.from(FILTER_GROUP_KEYS).flatMap((group) =>
        Object.entries(getGroupValue(group))
            .filter(([, value]) => hasFilterValue(value))
            .map(([key, value]) => ({
                group,
                key,
                path: `${group}.${camelCase(key)}`,
                value,
            })),
    );

    return [...attributeEntries, ...groupedEntries];
};

export const getSearchFilterActiveCount = (filters: unknown) => getSearchFilterEntries(filters).length;

export const getSearchFilterOperatorOptions = (value: unknown): SearchFilterOperatorOption[] =>
    typeof value === 'number' ? NUMBER_OPERATOR_OPTIONS : TEXT_OPERATOR_OPTIONS;

const defaultSearchFilterOperator = (value: unknown): SearchFilterOperator =>
    typeof value === 'number' ? 'equals' : 'contains';

export const getSearchFilterOperators = (filters: unknown): Record<string, SearchFilterOperator> => {
    if (!isFilterEnvelope(filters) || !isRecord(filters.metadata) || !isRecord(filters.metadata.operators)) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(filters.metadata.operators).filter(
            (entry): entry is [string, SearchFilterOperator] =>
                typeof entry[1] === 'string' && SEARCH_FILTER_OPERATORS.has(entry[1] as SearchFilterOperator),
        ),
    );
};

/** Return operator metadata relative to a Search domain's filter object. */
export const getScopedSearchFilterOperators = (filters: unknown, scope: readonly string[] = []) => {
    const operators = getSearchFilterOperators(filters);
    if (scope.length === 0) {
        return operators;
    }

    const prefix = `${scope.join('.')}.`;

    return Object.fromEntries(
        Object.entries(operators)
            .filter(([path]) => path.startsWith(prefix))
            .map(([path, operator]) => [path.slice(prefix.length), operator]),
    );
};

export const getSearchFilterOperator = (filters: unknown, path: string, value: unknown): SearchFilterOperator =>
    getSearchFilterOperators(filters)[path] ?? defaultSearchFilterOperator(value);

const combineFieldConditions = (conditions: JsonLogicRule[], join: 'and' | 'or' = 'or') => {
    if (conditions.length <= 1) {
        return conditions[0];
    }

    return compactJsonLogicConditions(conditions, join);
};

const buildScalarCondition = (field: string, value: unknown, operator: SearchFilterOperator): JsonLogicRule => {
    if (operator === 'contains') {
        return { contains: [jsonLogicField(field), value as JsonLogicRule] };
    }

    const jsonLogicOperator = {
        equals: '==',
        greaterThan: '>',
        lessThan: '<',
        notEquals: '!=',
    }[operator];

    return { [jsonLogicOperator]: [jsonLogicField(field), value as JsonLogicRule] };
};

const buildArrayConditions = (field: string, value: unknown[], operator: SearchFilterOperator) =>
    combineFieldConditions(
        value.filter(hasFilterValue).map((item) => buildScalarCondition(field, item, operator)),
        operator === 'notEquals' ? 'and' : 'or',
    );

const buildRangeConditions = (field: string, value: Record<string, unknown>) =>
    combineFieldConditions(
        [
            hasFilterValue(value.min)
                ? ({ '>=': [jsonLogicField(field), value.min as JsonLogicRule] } as JsonLogicRule)
                : undefined,
            hasFilterValue(value.max)
                ? ({ '<=': [jsonLogicField(field), value.max as JsonLogicRule] } as JsonLogicRule)
                : undefined,
        ].filter((condition): condition is JsonLogicRule => Boolean(condition)),
        'and',
    );

const buildObjectConditions = (
    field: string,
    value: Record<string, unknown>,
    operators: Record<string, SearchFilterOperator>,
    inheritedOperator?: SearchFilterOperator,
): JsonLogicRule | undefined => {
    const operator = operators[field] ?? inheritedOperator ?? defaultSearchFilterOperator(value);

    if ('min' in value || 'max' in value) {
        return buildRangeConditions(field, value);
    }

    if ('annualRevenue' in value && Array.isArray(value.annualRevenue)) {
        const nestedField = `${field}.annualRevenue`;

        return buildArrayConditions(nestedField, value.annualRevenue, operators[nestedField] ?? operator);
    }

    if ('country' in value || 'stateTerritory' in value || 'city' in value || 'zipCode' in value) {
        return combineFieldConditions(
            Object.entries(value)
                .filter(([, nestedValue]) => hasFilterValue(nestedValue))
                .map(([key, nestedValue]) => {
                    const nestedField = `${field}.${key}`;

                    return buildScalarCondition(nestedField, nestedValue, operators[nestedField] ?? operator);
                }),
            'or',
        );
    }

    if ('toggled' in value || 'categories' in value || 'startDate' in value || 'endDate' in value) {
        return combineFieldConditions(
            [
                value.toggled === true
                    ? ({ '==': [jsonLogicField(`${field}.toggled`), true] } as JsonLogicRule)
                    : undefined,
                Array.isArray(value.categories)
                    ? buildArrayConditions(
                          `${field}.categories`,
                          value.categories,
                          operators[`${field}.categories`] ?? operator,
                      )
                    : undefined,
                hasFilterValue(value.startDate)
                    ? ({ '>=': [jsonLogicField(`${field}.date`), value.startDate as JsonLogicRule] } as JsonLogicRule)
                    : undefined,
                hasFilterValue(value.endDate)
                    ? ({ '<=': [jsonLogicField(`${field}.date`), value.endDate as JsonLogicRule] } as JsonLogicRule)
                    : undefined,
            ].filter((condition): condition is JsonLogicRule => Boolean(condition)),
            'and',
        );
    }

    return combineFieldConditions(
        Object.entries(value)
            .filter(([, nestedValue]) => hasFilterValue(nestedValue))
            .map(([key, nestedValue]) => {
                const nestedField = `${field}.${key}`;

                if (Array.isArray(nestedValue)) {
                    return buildArrayConditions(nestedField, nestedValue, operators[nestedField] ?? operator);
                }

                if (isRecord(nestedValue)) {
                    return buildObjectConditions(nestedField, nestedValue, operators, operator);
                }

                return buildScalarCondition(nestedField, nestedValue, operators[nestedField] ?? operator);
            })
            .filter((condition): condition is JsonLogicRule => Boolean(condition)),
        'or',
    );
};

export const buildSearchFilterCondition = (
    entry: SearchFilterEntry,
    operators: Record<string, SearchFilterOperator>,
): JsonLogicRule | undefined => {
    const field = entry.group ? `${entry.group}.${entry.key}` : entry.key;
    const operator = operators[entry.path] ?? defaultSearchFilterOperator(entry.value);

    if (Array.isArray(entry.value)) {
        return buildArrayConditions(field, entry.value, operator);
    }

    if (isRecord(entry.value)) {
        return buildObjectConditions(field, entry.value, operators, operator);
    }

    if (hasFilterValue(entry.value)) {
        return buildScalarCondition(field, entry.value, operator);
    }

    return undefined;
};

const isSearchAdvancedFilterRule = (value: unknown): value is SearchAdvancedFilterRule =>
    isRecord(value) &&
    typeof value.key === 'string' &&
    typeof value.path === 'string' &&
    'value' in value &&
    typeof value.operator === 'string' &&
    SEARCH_FILTER_OPERATORS.has(value.operator as SearchFilterOperator) &&
    (value.sourcePath === undefined ||
        (Array.isArray(value.sourcePath) && value.sourcePath.every((part) => typeof part === 'string'))) &&
    (value.group === undefined || FILTER_GROUP_KEYS.has(value.group as SearchFilterGroup));

export const getSearchAdvancedFilter = (
    filters: unknown,
): AdvancedFilterDefinition<SearchAdvancedFilterRule> | undefined => {
    if (!isFilterEnvelope<SearchFiltersValue, SearchAdvancedFilterRule>(filters)) {
        return undefined;
    }

    return parseAdvancedFilterDefinition(filters.advancedFilter, isSearchAdvancedFilterRule);
};

export const getSearchAdvancedFilters = (filters: unknown): AdvancedFilterDefinition<SearchAdvancedFilterRule>[] => {
    if (!isFilterEnvelope<SearchFiltersValue, SearchAdvancedFilterRule>(filters)) {
        return [];
    }

    const definitions = parseAdvancedFilterDefinitions(filters.advancedFilters, isSearchAdvancedFilterRule);
    if (definitions.length) {
        return definitions;
    }

    const legacyDefinition = parseAdvancedFilterDefinition(filters.advancedFilter, isSearchAdvancedFilterRule);

    return legacyDefinition ? [legacyDefinition] : [];
};

/**
 * Keeps only conditions that can be evaluated by a specific federated Search
 * provider. This prevents a workspace-only condition from causing an
 * unrelated provider request while retaining the user's group ordering and
 * connectors for the selected domain.
 */
export const getScopedSearchAdvancedFilter = (
    filters: unknown,
    domain: string,
): AdvancedFilterDefinition<SearchAdvancedFilterRule> | undefined => {
    const definition = getSearchAdvancedFilter(filters);
    if (!definition) {
        return undefined;
    }

    const groups = definition.groups
        .map((group) => ({
            ...group,
            conditions: group.conditions.filter(({ rule }) => {
                const sourcePath = rule.sourcePath ?? [rule.path];

                return sourcePath[0] === 'searchDomains' ? sourcePath[1] === domain : domain === 'companies';
            }),
        }))
        .filter((group) => group.conditions.length > 0);

    return groups.length ? { ...definition, groups } : undefined;
};

export const getScopedSearchAdvancedFilters = (
    filters: unknown,
    domain: string,
): AdvancedFilterDefinition<SearchAdvancedFilterRule>[] =>
    getSearchAdvancedFilters(filters)
        .map((definition) => {
            const groups = definition.groups
                .map((group) => ({
                    ...group,
                    conditions: group.conditions.filter(({ rule }) => {
                        const sourcePath = rule.sourcePath ?? [rule.path];

                        return sourcePath[0] === 'searchDomains' ? sourcePath[1] === domain : domain === 'companies';
                    }),
                }))
                .filter((group) => group.conditions.length > 0);

            return groups.length ? { ...definition, groups } : undefined;
        })
        .filter((definition): definition is AdvancedFilterDefinition<SearchAdvancedFilterRule> => Boolean(definition));

const buildSearchAdvancedFilterRule = (rule: SearchAdvancedFilterRule) =>
    buildSearchFilterCondition(rule, { [rule.path]: rule.operator });

export const buildSearchAdvancedFilterJsonLogic = (
    advancedFilter: AdvancedFilterDefinition<SearchAdvancedFilterRule> | null | undefined,
): JsonLogicRule =>
    buildAdvancedFiltersJsonLogic(advancedFilter ? [advancedFilter] : [], buildSearchAdvancedFilterRule) ?? {};

export const buildSearchAdvancedFiltersJsonLogic = (
    advancedFilters: readonly AdvancedFilterDefinition<SearchAdvancedFilterRule>[] | null | undefined,
): JsonLogicRule => buildAdvancedFiltersJsonLogic(advancedFilters, buildSearchAdvancedFilterRule) ?? {};

export const buildSearchFilterJsonLogic = (
    filters: unknown,
    matchingPattern: SearchFilterJoin = 'any',
    operators = getSearchFilterOperators(filters),
    requestedAdvancedFilters:
        | AdvancedFilterDefinition<SearchAdvancedFilterRule>
        | readonly AdvancedFilterDefinition<SearchAdvancedFilterRule>[]
        | null
        | undefined = getSearchAdvancedFilters(filters),
): JsonLogicRule => {
    const advancedFilters = Array.isArray(requestedAdvancedFilters)
        ? requestedAdvancedFilters
        : requestedAdvancedFilters
          ? [requestedAdvancedFilters]
          : [];
    const conditions = getSearchFilterEntries(filters)
        .map((entry) => buildSearchFilterCondition(entry, operators))
        .filter((condition): condition is JsonLogicRule => Boolean(condition));

    const simpleExpression = compactJsonLogicConditions(conditions, matchingPattern === 'all' ? 'and' : 'or');
    const advancedExpression = buildSearchAdvancedFiltersJsonLogic(advancedFilters);

    if (isJsonLogicRuleEmpty(advancedExpression)) {
        return simpleExpression;
    }

    if (isJsonLogicRuleEmpty(simpleExpression)) {
        return advancedExpression;
    }

    return compactJsonLogicConditions([simpleExpression, advancedExpression], 'and');
};

export const getSearchFilterJoin = (filters: unknown): SearchFilterJoin => {
    if (!isFilterEnvelope(filters) || !isRecord(filters.metadata)) {
        return 'any';
    }

    return filters.metadata.matchingPattern === 'all' ? 'all' : 'any';
};

const getPathValue = (value: SearchFiltersValue, path: string) =>
    path
        .split('.')
        .reduce<unknown>((currentValue, key) => (isRecord(currentValue) ? currentValue[key] : undefined), value);

const pruneSearchFilterOperators = (legacy: SearchFiltersValue, operators: Record<string, SearchFilterOperator>) =>
    Object.fromEntries(Object.entries(operators).filter(([path]) => hasFilterValue(getPathValue(legacy, path))));

export const buildSearchFilterEnvelope = (
    filters: unknown,
    matchingPattern = getSearchFilterJoin(filters),
    requestedOperators = getSearchFilterOperators(filters),
    requestedAdvancedFilters:
        | AdvancedFilterDefinition<SearchAdvancedFilterRule>
        | readonly AdvancedFilterDefinition<SearchAdvancedFilterRule>[]
        | null
        | undefined = getSearchAdvancedFilters(filters),
) => {
    const legacy = unwrapSearchFilters(filters);
    const operators = pruneSearchFilterOperators(legacy, requestedOperators);
    const advancedFilters = Array.isArray(requestedAdvancedFilters)
        ? [...requestedAdvancedFilters]
        : requestedAdvancedFilters
          ? [requestedAdvancedFilters]
          : [];

    return createFilterEnvelope<SearchFiltersValue, SearchAdvancedFilterRule>({
        ...(advancedFilters.length === 1 ? { advancedFilter: advancedFilters[0] } : {}),
        advancedFilters,
        domain: 'search',
        jsonLogic: buildSearchFilterJsonLogic(legacy, matchingPattern, operators, advancedFilters),
        legacy,
        metadata: {
            matchingPattern,
            ...(Object.keys(operators).length ? { operators } : {}),
        },
    });
};

export const updateSearchFilterEnvelope = (
    filters: unknown,
    update: (legacy: SearchFiltersValue) => SearchFiltersValue,
) =>
    buildSearchFilterEnvelope(
        update({ ...unwrapSearchFilters(filters) }),
        getSearchFilterJoin(filters),
        getSearchFilterOperators(filters),
        getSearchAdvancedFilters(filters),
    );

export const updateSearchFilterOperator = (filters: unknown, path: string, operator: SearchFilterOperator) =>
    buildSearchFilterEnvelope(
        filters,
        getSearchFilterJoin(filters),
        {
            ...getSearchFilterOperators(filters),
            [path]: operator,
        },
        getSearchAdvancedFilters(filters),
    );

export const updateSearchAdvancedFilter = (
    filters: unknown,
    advancedFilter: AdvancedFilterDefinition<SearchAdvancedFilterRule> | undefined,
) =>
    buildSearchFilterEnvelope(
        filters,
        getSearchFilterJoin(filters),
        getSearchFilterOperators(filters),
        advancedFilter ? [advancedFilter] : [],
    );

export const updateSearchAdvancedFilters = (
    filters: unknown,
    advancedFilters: AdvancedFilterDefinition<SearchAdvancedFilterRule>[],
) =>
    buildSearchFilterEnvelope(
        filters,
        getSearchFilterJoin(filters),
        getSearchFilterOperators(filters),
        advancedFilters,
    );

export const isSearchFilterEnvelope = (filters: unknown) =>
    isFilterEnvelope<SearchFiltersValue>(filters) && filters.domain === 'search';
